import React from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { clsx } from 'clsx';
import type { AlertProps } from '../types/interfaces';

const Alert: React.FC<AlertProps> = ({
  type,
  title,
  message,
  children,
  dismissible = false,
  onDismiss,
  className = ''
}) => {
  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info
  };

  const Icon = icons[type];

  const alertClasses = clsx(
    'alert',
    {
      'alert-success': type === 'success',
      'alert-error': type === 'error',
      'alert-warning': type === 'warning',
      'alert-info': type === 'info'
    },
    className
  );

  return (
    <div className={alertClasses} role="alert">
      <div className="flex items-start">
        <Icon className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" aria-hidden="true" />
        
        <div className="flex-1 min-w-0">
          {title && (
            <div className="font-medium mb-1">
              {title}
            </div>
          )}
          <div className={clsx({
            'text-sm': title,
            'font-medium': !title
          })}>
            {children || message}
          </div>
        </div>
        
        {dismissible && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="ml-3 flex-shrink-0 p-1 rounded-md hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current transition-colors duration-200"
            aria-label="Dismiss alert"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

Alert.displayName = 'Alert';

export default Alert;